import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, AlertTriangle } from "lucide-react";
import { api } from "@/lib/museo";
import { track } from "@/lib/analytics";
import { moderateClient, moderateSignatureClient } from "@/lib/moderation";
import { toast } from "sonner";

const MAX = 180;
const MAX_FIRMA = 30;

const ESEMPI = [
  "Perdonami per quello che ho fatto.",
  "Prometto che questa volta scelgo me.",
  "Ti penso ancora, anche se non dovrei.",
  "Papà, avrei voluto renderti fiero.",
  "Mi dispiace non avertelo detto prima.",
  "Grazie per essere rimasto.",
  "Un giorno ti racconterò tutto.",
  "Vorrei aver avuto più tempo.",
];

const CHECKS = [
  "Confermo che la promessa non contiene dati personali, cognomi, numeri di telefono, indirizzi o informazioni private.",
  "Confermo che la promessa non contiene minacce, odio, spam o contenuti offensivi.",
  "Capisco che la promessa sarà pubblica e visibile a tutti.",
];

export const PromiseModal = ({ open, onClose }) => {
  const [testo, setTesto] = useState("");
  const [firmato, setFirmato] = useState(false);
  const [firma, setFirma] = useState("");
  const [checks, setChecks] = useState([false, false, false]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setTesto("");
      setFirmato(false);
      setFirma("");
      setChecks([false, false, false]);
      setLoading(false);
    }
  }, [open]);

  const allChecked = checks.every(Boolean);
  const mod = moderateClient(testo);
  const firmaMod = firmato ? moderateSignatureClient(firma) : { ok: true };
  const remaining = MAX - testo.length;
  const counterColor = remaining <= 20 ? "text-museo-accent" : "text-museo-muted";
  const canSubmit =
    testo.trim().length > 0 &&
    testo.length <= MAX &&
    allChecked &&
    mod.ok &&
    firmaMod.ok &&
    !loading;

  const toggle = (i) => {
    setChecks((c) => c.map((v, idx) => (idx === i ? !v : v)));
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setLoading(true);
    try {
      const res = await api.post("/checkout/session", {
        testo: testo.trim(),
        firma: firmato ? firma.trim() : "",
        origin_url: window.location.origin,
      });
      track("begin_checkout", { currency: "EUR", value: 1.99 });
      window.location.href = res.data.url;
    } catch (e) {
      setLoading(false);
      const msg = e?.response?.data?.detail || "Si è verificato un errore. Riprova.";
      toast.error(msg);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-start md:items-center justify-center overflow-y-auto py-8 px-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div
            className="fixed inset-0 backdrop-blur-xl bg-museo-bg/85"
            onClick={onClose}
            data-testid="modal-overlay"
          />
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            className="relative z-10 bg-museo-surface border border-museo-border p-8 md:p-12 max-w-2xl w-full rounded-none md:rounded-sm my-auto"
            data-testid="promise-modal"
          >
            <button
              onClick={onClose}
              data-testid="modal-close-button"
              className="absolute top-5 right-5 text-museo-muted hover:text-museo-accent transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            <h2 className="font-serif text-3xl md:text-4xl text-museo-text mb-4">
              Lascia il tuo messaggio nel museo
            </h2>
            <p className="text-sm md:text-base text-museo-muted font-light leading-relaxed mb-8">
              Scrivi una promessa, una scusa, un perdono, un addio o un messaggio che non hai mai detto.
              Sarà anonimo, pubblico e visibile nel Museo delle Promesse.
            </p>

            <div className="mb-6">
              <textarea
                data-testid="promise-textarea"
                value={testo}
                onChange={(e) => setTesto(e.target.value.slice(0, MAX))}
                placeholder="Scrivi qui il tuo messaggio…"
                rows={3}
                className="w-full bg-transparent border-b border-museo-border text-museo-text text-2xl md:text-3xl font-serif py-3 focus:border-museo-accent focus:outline-none placeholder:text-museo-muted/40 resize-none"
              />
              <div className="flex justify-end mt-2">
                <span data-testid="char-counter" className={`font-mono text-xs ${counterColor}`}>
                  {testo.length} / {MAX}
                </span>
              </div>
              {testo.trim() && !mod.ok && (
                <div
                  data-testid="moderation-warning"
                  className={`mt-4 flex items-start gap-2 text-sm font-light p-3 border rounded-none md:rounded-sm ${
                    mod.type === "self_harm"
                      ? "border-museo-accent/40 text-museo-accent bg-museo-accent/5"
                      : "border-museo-error/50 text-museo-error/90 bg-museo-error/5"
                  }`}
                >
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>{mod.message}</span>
                </div>
              )}
            </div>

            {/* Esempi */}
            <div className="mb-8" data-testid="esempi-frasi">
              <p className="text-xs uppercase tracking-[0.2em] text-museo-muted mb-3">Puoi scrivere qualsiasi cosa</p>
              <div className="flex flex-wrap gap-2">
                {ESEMPI.map((e, i) => (
                  <button
                    key={i}
                    type="button"
                    data-testid={`esempio-${i}`}
                    onClick={() => setTesto(e.slice(0, MAX))}
                    className="text-left text-xs md:text-sm font-serif italic text-museo-muted hover:text-museo-accent border border-museo-border hover:border-museo-accent/50 rounded-none md:rounded-sm px-3 py-1.5 transition-colors duration-300"
                  >
                    “{e}”
                  </button>
                ))}
              </div>
            </div>

            {/* Firma */}
            <div className="mb-8">
              <div className="flex items-center gap-1 mb-4 border border-museo-border rounded-none md:rounded-sm p-1 w-full sm:w-auto sm:inline-flex">
                <button
                  type="button"
                  data-testid="firma-anonimo"
                  onClick={() => setFirmato(false)}
                  className={`flex-1 sm:flex-none px-5 py-2 text-xs uppercase tracking-[0.15em] transition-colors duration-300 ${
                    !firmato ? "bg-museo-accent text-museo-bg" : "text-museo-muted hover:text-museo-text"
                  }`}
                >
                  Anonimo
                </button>
                <button
                  type="button"
                  data-testid="firma-firmato"
                  onClick={() => setFirmato(true)}
                  className={`flex-1 sm:flex-none px-5 py-2 text-xs uppercase tracking-[0.15em] transition-colors duration-300 ${
                    firmato ? "bg-museo-accent text-museo-bg" : "text-museo-muted hover:text-museo-text"
                  }`}
                >
                  Firma
                </button>
              </div>
              {firmato && (
                <div>
                  <input
                    data-testid="firma-input"
                    value={firma}
                    onChange={(e) => setFirma(e.target.value.slice(0, MAX_FIRMA))}
                    placeholder="Un nome, un'iniziale, un soprannome…"
                    className="w-full bg-transparent border-b border-museo-border text-museo-text text-lg font-serif py-2 focus:border-museo-accent focus:outline-none placeholder:text-museo-muted/40"
                  />
                  <div className="flex justify-end mt-1">
                    <span className="font-mono text-xs text-museo-muted">{firma.length} / {MAX_FIRMA}</span>
                  </div>
                  {firma.trim() && !firmaMod.ok && (
                    <div data-testid="firma-warning" className="mt-2 flex items-start gap-2 text-sm font-light text-museo-error/90">
                      <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                      <span>{firmaMod.message}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="space-y-4 mb-8">
              {CHECKS.map((label, i) => (
                <label
                  key={i}
                  data-testid={`promise-check-${i}`}
                  className="flex items-start gap-3 cursor-pointer group"
                  onClick={(e) => {
                    e.preventDefault();
                    toggle(i);
                  }}
                >
                  <span
                    className={`mt-0.5 w-5 h-5 shrink-0 border flex items-center justify-center transition-all duration-300 ${
                      checks[i] ? "bg-museo-accent border-museo-accent" : "border-museo-border group-hover:border-museo-accent"
                    }`}
                  >
                    {checks[i] && (
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path d="M2 6L5 9L10 3" stroke="#0C0B0A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </span>
                  <span className="text-xs md:text-sm text-museo-muted font-light leading-relaxed">{label}</span>
                </label>
              ))}
            </div>

            <button
              data-testid="continue-payment-button"
              disabled={!canSubmit}
              onClick={handleSubmit}
              className="w-full bg-museo-accent text-museo-bg px-8 py-4 text-sm tracking-[0.2em] uppercase font-medium hover:bg-museo-text transition-all duration-500 rounded-none md:rounded-sm disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Attendi…
                </>
              ) : (
                "Continua al pagamento — 1,99 €"
              )}
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
